/* ---------------------------------------------------------------------------
   SFD Shelter configurator

   One script for every shelter: cycle, smoking, trolley, bin, school. It makes
   no assumptions about which options a product has or what they are called.

   TWO OPTION SYSTEMS, AND BOTH STAY

     Shopify variants  fixing, finish, size, capacity, side panels, rack, gate
     Easify Options    colour, bins, seating, end panels, installation

   Easify is not reimplemented here. The section renders anchors the app can
   mount into and this script moves what it mounts into the buy box, so the
   app keeps its own controls, its own conditional logic and its own cart
   properties.

   NOTHING HERE BLOCKS A SALE

   Lee's rule: nothing in the configurator stops the buying. Add to basket is
   never disabled for an unanswered option. If a choice has not been made the
   script falls back to the first available value rather than refusing. Easify
   may still mark one of its own fields required; because the add goes through
   /cart/add.js rather than Easify's form submit, that does not block either.

   NO REFETCHING

   Shelters carry at most two dozen variants, so the whole variant table is in
   the payload and every combination is resolved in the browser. No Section
   Rendering round trip, no spinner between clicks.

   THE OFFER IS ONE PILL, AND IT IS DERIVED FROM THE PRICE ABOVE IT

   Lee's call, 6 Oct 2026. [data-shc-promo] is a single span in the price
   column and the script writes one string into it: the offer label, the
   saving, and the old price struck through. An earlier attempt split it into
   a chip above the price and a separate was line below, and that duplicated
   the price label and ran the was line onto the end of the ex VAT figure.
   Keep it as one element: if the wording needs to change, change the string,
   do not add elements around it.

   The Was figure is the displayed total plus the saving, never the bare
   variant compare-at. See the note on the pill in paint().
   --------------------------------------------------------------------------- */
(function () {
  'use strict';

  var root = document.querySelector('[data-shc]');
  if (!root) return;

  var VAT_RATE = (parseFloat(root.getAttribute('data-vat-rate')) || 20) / 100;
  var AFTER_ADD = root.getAttribute('data-after-add') || 'drawer';
  var QUOTE_URL = root.getAttribute('data-quote-url') || '';

  var S, COPY;
  try { S = JSON.parse(root.querySelector('[data-shc-state]').textContent); } catch (e) { return; }
  try { COPY = JSON.parse(root.querySelector('[data-shc-copy]').textContent); } catch (e) { COPY = {}; }

  /* ---------------- small helpers ---------------- */
  function esc(v) {
    return String(v == null ? '' : v)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
  }
  function fill(tpl, map) {
    return String(tpl || '').replace(/\{(\w+)\}/g, function (m, k) {
      return map[k] == null ? '' : map[k];
    });
  }
  function table(raw) {
    var out = {};
    String(raw || '').split('|').forEach(function (pair) {
      var i = pair.indexOf('=');
      if (i < 0) return;
      out[pair.slice(0, i).trim().toLowerCase()] = pair.slice(i + 1).trim();
    });
    return out;
  }
  function list(raw) {
    return String(raw || '').split('|').map(function (x) { return x.trim(); }).filter(Boolean);
  }
  function money(pence) {
    return '£' + (pence / 100).toLocaleString('en-GB',
      { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  function vatInc() { return document.documentElement.classList.contains('vat-inc'); }
  function headline(p) { return money(vatInc() ? Math.round(p * (1 + VAT_RATE)) : p); }
  function secondary(p) {
    return vatInc() ? money(p) + ' ex VAT'
                    : money(Math.round(p * (1 + VAT_RATE))) + ' inc VAT';
  }

  var VALUE_MAP = table(COPY.valueMap);
  var SWATCH_MAP = table(COPY.swatchMap);
  var NOTE_MAP = table(COPY.noteMap);
  var ROLE_ORDER = list(COPY.roleOrder);

  /* Display label only. The cart always gets the real option value. */
  function label(v) {
    var m = VALUE_MAP[String(v).trim().toLowerCase()];
    return m || v;
  }
  function swatch(v) {
    return SWATCH_MAP[String(label(v)).trim().toLowerCase()] || '';
  }
  function note(v) {
    return NOTE_MAP[String(v).trim().toLowerCase()] ||
           NOTE_MAP[String(label(v)).trim().toLowerCase()] || '';
  }
  function roleRank(r) {
    var i = ROLE_ORDER.indexOf(r);
    return i < 0 ? 900 + ROLE_ORDER.length : i;
  }

  /* ---------------- selection state ---------------- */
  /* One chosen value per option position, seeded from the first variant that
     is actually available, falling back to the first variant. */
  var chosen = [];

  function seed() {
    /* A ?variant= link, from Google Shopping or an email, should land on the
       variant it names rather than the first one in the list. */
    var want = null;
    try {
      var q = new URL(window.location.href).searchParams.get('variant');
      if (q) want = S.variants.filter(function (x) { return String(x.id) === String(q); })[0];
    } catch (e) { want = null; }
    var v = want || S.variants.filter(function (x) { return x.av; })[0] || S.variants[0];
    chosen = v ? v.o.slice() : S.opts.map(function (o) { return o.values[0]; });
  }

  /* Easify decides which of its conditional options apply by looking up the
     current variant in window.meta.product.variants and reading its title.
     Shopify's copy of that list carries public_title but no title, so the
     lookup comes back empty and Easify falls back to a paginated call to its
     own API, which is why a colour option could take ten seconds to appear or
     not appear at all. Fill the missing field from the configurator's own
     variant table. Nothing is overwritten, so analytics and anything else
     reading meta see exactly what they saw before. */
  function fillMetaTitles() {
    try {
      var mv = window.meta && window.meta.product && window.meta.product.variants;
      if (!mv || !mv.length) return;
      var byId = {};
      S.variants.forEach(function (v) { byId[String(v.id)] = v.o.join(' / '); });
      mv.forEach(function (v) {
        if (v && v.title == null) v.title = byId[String(v.id)] || v.public_title || '';
      });
    } catch (e) { /* only affects how quickly Easify reacts */ }
  }

  function variantFor(vals) {
    for (var i = 0; i < S.variants.length; i++) {
      var v = S.variants[i], ok = true;
      for (var j = 0; j < vals.length; j++) { if (v.o[j] !== vals[j]) { ok = false; break; } }
      if (ok) return v;
    }
    return null;
  }

  function current() { return variantFor(chosen); }

  /* Choosing a value must never dead-end. If the straight swap is not a real
     variant, keep the new value and move the other options to the nearest
     combination that is, preferring available ones. */
  function choose(pos, val) {
    var want = chosen.slice();
    want[pos] = val;
    if (variantFor(want)) { chosen = want; return; }

    var best = null;
    S.variants.forEach(function (v) {
      if (v.o[pos] !== val) return;
      var score = 0;
      for (var j = 0; j < v.o.length; j++) { if (v.o[j] === chosen[j]) score += 2; }
      if (v.av) score += 1;
      if (!best || score > best.s) best = { s: score, v: v };
    });
    if (best) chosen = best.v.o.slice();
  }

  /* ---------------- delivery, the same rule as the bollards ---------------- */
  /* Estimate gives the near end of the window, buffer days the far end, and
     weekends are skipped at both ends. Lead time is per variant where the
     variant carries its own, otherwise the product's. */
  var DAY = 86400000;
  function skipWeekend(d) {
    var g = d.getDay();
    if (g === 6) d.setDate(d.getDate() + 2);
    if (g === 0) d.setDate(d.getDate() + 1);
    return d;
  }
  function ordinal(n) {
    var a = n % 10, b = n % 100;
    if (b >= 11 && b <= 13) return 'th';
    return a === 1 ? 'st' : a === 2 ? 'nd' : a === 3 ? 'rd' : 'th';
  }
  var DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var MONS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
              'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  function fmt(d, short) {
    var n = d.getDate();
    return short ? n + ' ' + MONS[d.getMonth()]
                 : DAYS[d.getDay()] + ' ' + n + ordinal(n) + ' ' + MONS[d.getMonth()];
  }
  function window_(v) {
    var lead = (v && v.lead > 0) ? v.lead : (S.lead || 0);
    if (!lead) return null;
    var buf = S.buffer > 0 ? S.buffer : 2;
    var early = skipWeekend(new Date(Date.now() + (lead - 1) * DAY));
    var late = skipWeekend(new Date(early.getTime() + buf * DAY));
    return { lead: lead, early: early, late: late };
  }

  function delivHtml(v) {
    var w = window_(v);
    if (!w) return '';
    var when = fill(COPY.byWindow, {
      from: '<b>' + esc(fmt(w.early)) + '</b>',
      to: '<b>' + esc(fmt(w.late)) + '</b>'
    });
    var sub = fill(COPY.dispatch, { lead: w.lead });
    return '<p class="shc-deliv"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4"/></svg>' +
      '<span>' + when + (sub ? '<em>' + esc(sub) + '</em>' : '') + '</span></p>';
  }

  function stockHtml(v) {
    if (!v) return '';
    return v.av
      ? '<p class="shc-stock shc-stock--in"><span class="shc-dot"></span>' +
        esc(COPY.inStock || 'Available to order') + '</p>'
      : '<p class="shc-stock shc-stock--out"><span class="shc-dot"></span>' +
        esc(COPY.outStock || 'Ask us for a lead time') + '</p>';
  }

  /* ---------------- Easify ---------------- */
  /* Easify mounts itself into the anchors the section renders server side.
     This watches for that, then moves the mounted option set into the slot in
     the buy box. Moving a node leaves its listeners intact, so the app keeps
     working exactly as it does on any other product page.

     Most shelters have no option set at all, only about 37 of 127, so nothing
     appearing is the normal case and the block simply stays hidden. */
  function mountEasify() {
    var slot = root.querySelector('[data-shc-easify]');
    var host = root.querySelector('[data-shc-anchors]');
    if (!slot || !host) return;

    var SEL = '.tpo_option-set-wrapper, [id^="tpo_option-set-container-"]';
    var waited = 0;

    (function watch() {
      var set = host.querySelector(SEL) || slot.querySelector(SEL);
      if (set) {
        if (set.parentElement !== slot) slot.appendChild(set);
        afterMount(true);
        return;
      }
      waited += 300;
      if (waited < 8000) { setTimeout(watch, 300); return; }
      afterMount(false);
    })();
  }

  /* Keep the hidden id in step with the configurator. The form never submits,
     but Easify and anything else reading it should see the right variant. */
  function syncVariantInput() {
    var v = current();
    var el = root.querySelector('[data-shc-vid]');
    if (el && v) el.value = v.id;
    if (v) setVariantUrl(v.id);
  }

  /* Easify works out which of its conditional options to show by reading
     ?variant= off the URL, from inside a MutationObserver on the whole
     document. The theme's own product page writes that parameter whenever a
     variant is picked, so this does the same. Without it Easify judges every
     condition against the first available variant, which is why the colour
     swatches stayed hidden on a powder coated shelter. It is also what makes
     a copied URL reopen on the right configuration. */
  var lastVid = null;
  function setVariantUrl(id) {
    if (String(id) === String(lastVid)) return;
    lastVid = id;
    try {
      var u = new URL(window.location.href);
      u.searchParams.set('variant', id);
      window.history.replaceState({}, '', u.toString());
    } catch (e) { return; }
    /* The observer only looks when something in the document changes, so give
       it a change to see. Cheap, and gone on the next tick. */
    var ping = document.createComment('shc');
    document.body.appendChild(ping);
    setTimeout(function () { if (ping.parentNode) ping.parentNode.removeChild(ping); }, 0);
  }

  function afterMount(ok) {
    var slot = root.querySelector('[data-shc-easify]');
    var wrap = slot && slot.closest('[data-shc-easifywrap]');
    /* Nothing mounted means this product has no option set. Hide the whole
       block rather than leaving an empty heading on the page. */
    if (!ok && wrap) wrap.hidden = true;
    if (ok) { proxyEasify(); showExtras(); watchEasify(); }
    paint();
    if (slot) {
      slot.addEventListener('change', function () { resyncProxies(); paint(); });
      slot.addEventListener('input', paint);
      slot.addEventListener('click', function () {
        setTimeout(function () { resyncProxies(); paint(); }, 60);
      });
    }
  }

  /* Easify's running add-on total, in pounds.

     properties[_tpo_price] is the one to trust: it is the plain number the app
     puts on the cart line. tpo_total-additional-price is only filled in when
     Easify draws its own price display, which this page does not use, so on
     its own it reads a flat zero however much has been added. It is kept as a
     fallback in case a future version stops writing the property. */
  function easifyAddonPence() {
    var el = document.querySelector('[name="properties[_tpo_price]"]') ||
             document.querySelector('[name="tpo_total-additional-price"]');
    if (!el) return 0;
    var n = parseFloat(String(el.value).replace(/[^0-9.\-]/g, ''));
    return isFinite(n) ? Math.round(n * 100) : 0;
  }

  /* Every visible Easify choice, for the configuration line and the basket. */
  function easifyProps() {
    var out = {};
    try {
      if (typeof window.getEasifyProperties === 'function') {
        var p = window.getEasifyProperties();
        if (p && typeof p === 'object') Object.keys(p).forEach(function (k) { out[k] = p[k]; });
      }
    } catch (e) { /* the basket add matters more than the extras */ }
    document.querySelectorAll('[name^="properties["]').forEach(function (i) {
      if (i.type === 'radio' || i.type === 'checkbox') { if (!i.checked) return; }
      var k = i.name.slice(11, -1);
      if (i.value !== '' && i.value != null) out[k] = i.value;
    });
    return out;
  }

  /* Easify's own bookkeeping, not a customer choice.

     A cart property whose name starts with an underscore is a different
     thing: that is Shopify's convention for a property the shopper does not
     see, and Easify uses it for its add-on product options, _Bins and
     _Benches among them. Those are real choices and belong on the page even
     though the basket hides them. */
  var TPO_KEY = /^_tpo_/;

  /* Easify writes the chosen value with its own price in brackets, which is
     exactly what the breakdown needs: a line for each extra rather than one
     lump sum. */
  function easifyLines() {
    var p = easifyProps(), out = [];
    Object.keys(p).forEach(function (k) {
      if (TPO_KEY.test(k)) return;
      var raw = String(p[k]);
      var v = plain(raw);
      if (!v || NO_VALUE.test(v)) return;
      var m = raw.match(/[[(]\s*\+?\s*£\s*([0-9][0-9,.]*)\s*[)\]]\s*$/);
      out.push({
        name: k, value: v,
        pence: m ? Math.round(parseFloat(m[1].replace(/,/g, '')) * 100) : 0
      });
    });
    return out;
  }

  function easifySummary() {
    return easifyLines().map(function (l) { return l.value; });
  }

  /* ---------------- Easify: yes and no for optional extras ---------------- */
  /* Where an Easify option offers "Not Required" and one real choice, it
     should read as a Yes and a No, not a dropdown. Where it offers more than
     one real choice, No is still Not Required and Yes opens the choices
     underneath.

     Easify's own control stays on the page and keeps doing the work: these
     buttons click it. Its pricing, its conditional logic and the properties
     it writes on the cart line are all untouched, which is what makes this
     safe to apply to every product at once. Store-wide the pattern covers 70
     options, 46 of them a clean two way choice.

     If the markup is ever not the shape this expects, the option is left
     alone and the ordinary dropdown shows. Nothing breaks, it just looks like
     the app again. */
  var NO_VALUE = /^(not required|none|no|n\/a|not needed|without)$/i;

  /* "Standard End Panels [ +£324.51 ]" reads better as "Standard End
     Panels". The price is shown separately, so it does not want saying twice. */
  function plain(t) {
    return String(t == null ? '' : t)
      .replace(/\s*[[(]\s*\+?[^)\]]*[)\]]\s*$/, '')
      .replace(/\s+/g, ' ').trim();
  }
  function btnLabel(b) {
    var el = b.querySelector('.tpo_option_type_label');
    return plain(el ? el.textContent : b.textContent);
  }
  function btnPrice(b) {
    var el = b.querySelector('.tpo_additional-price');
    var t = el ? el.textContent.replace(/[()]/g, '').trim() : '';
    return (t && !/^£?0(\.00)?$/.test(t)) ? t : '';
  }

  var proxying = false;
  /* Every proxied option registers how to re-read itself, because Easify can
     take longer than a tick to write its own field and because it also
     changes selections on its own when a condition fires. */
  var syncers = [];
  function resyncProxies() {
    /* Drop any whose option Easify has since thrown away and redrawn. */
    syncers = syncers.filter(function (f) { return f.box && f.box.isConnected !== false; });
    syncers.forEach(function (f) { try { f(); } catch (e) { /* gone */ } });
  }

  function proxyOne(box) {
    if (box.querySelector('[data-shc-yn]')) return;
    if (!box.querySelector('.tpo_option-input-wrapper-dropdown')) return;

    var btns = [].slice.call(box.querySelectorAll('button.tpo_option-type-selection'));
    if (btns.length < 2) return;

    var noBtn = null, yes = [];
    btns.forEach(function (b) {
      if (!noBtn && NO_VALUE.test(btnLabel(b))) { noBtn = b; return; }
      yes.push(b);
    });
    if (!noBtn || !yes.length) return;

    var one = yes.length === 1;
    var yesNote = one
      ? '<span>' + esc(btnLabel(yes[0])) + '</span>' +
        (btnPrice(yes[0]) ? '<em>+' + esc(btnPrice(yes[0]).replace(/^\+/, '')) + '</em>' : '')
      : '<span>' + esc(fill(COPY.ynChoose || '{n} to choose from', { n: yes.length })) + '</span>';

    var tiles = one ? '' : yes.map(function (b, i) {
      return '<button type="button" class="shc-opt" data-shc-ynpick-i="' + i +
        '" aria-pressed="false"><b>' + esc(btnLabel(b)) + '</b>' +
        (btnPrice(b) ? '<em>+' + esc(btnPrice(b).replace(/^\+/, '')) + '</em>' : '') +
        '</button>';
    }).join('');

    var cols = yes.length > 3 ? 'shc-opts--4' : yes.length === 3 ? 'shc-opts--3' : 'shc-opts--2';

    var ui = document.createElement('div');
    ui.className = 'shc-yn';
    ui.setAttribute('data-shc-yn', '');
    ui.innerHTML =
      '<div class="shc-opts shc-opts--2">' +
        '<button type="button" class="shc-opt" data-shc-yn-no aria-pressed="true"><b>' +
          esc(COPY.ynNo || 'No') + '</b><span>' +
          esc(COPY.ynNoNote || 'Not required') + '</span></button>' +
        '<button type="button" class="shc-opt" data-shc-yn-yes aria-pressed="false"><b>' +
          esc(COPY.ynYes || 'Yes') + '</b>' + yesNote + '</button>' +
      '</div>' +
      (one ? '' : '<div class="shc-ynpick shc-opts ' + cols + '" data-shc-ynpick hidden>' +
        tiles + '</div>');

    var lastYes = 0;

    function sync() {
      var inp = box.querySelector('input.tpo_option-input');
      var val = plain(inp ? inp.value : '');
      var isNo = !val || NO_VALUE.test(val);
      ui.querySelector('[data-shc-yn-no]').setAttribute('aria-pressed', isNo ? 'true' : 'false');
      ui.querySelector('[data-shc-yn-yes]').setAttribute('aria-pressed', isNo ? 'false' : 'true');
      var pick = ui.querySelector('[data-shc-ynpick]');
      if (!pick) return;
      pick.hidden = isNo;
      [].forEach.call(pick.children, function (t, i) {
        var on = !isNo && btnLabel(yes[i]) === val;
        t.setAttribute('aria-pressed', on ? 'true' : 'false');
        if (on) lastYes = i;
      });
    }

    /* Click the app's own button and let it do everything it normally does.
       Easify does not always have its own field written by the next tick, so
       read it back twice: once quickly so the page feels immediate, once
       later so the right choice ends up highlighted. */
    function hit(b) {
      if (!b) return;
      proxying = true;
      try { b.click(); } catch (e) { /* leave the dropdown as it was */ }
      setTimeout(function () { resyncProxies(); showExtras(); paint(); }, 180);
      setTimeout(function () {
        proxying = false;
        resyncProxies(); showExtras(); paint();
      }, 700);
    }

    ui.addEventListener('click', function (e) {
      var b = e.target.closest('button');
      if (!b) return;
      if (b.hasAttribute('data-shc-yn-no')) { hit(noBtn); return; }
      if (b.hasAttribute('data-shc-yn-yes')) { hit(yes[lastYes] || yes[0]); return; }
      var i = b.getAttribute('data-shc-ynpick-i');
      if (i != null) { lastYes = parseInt(i, 10); hit(yes[lastYes]); }
    });

    /* The dropdown is not a direct child of the option container, it sits two
       levels down inside the label block, so insert against its own parent. */
    var ref = box.querySelector('.tpo_option-input-wrapper');
    if (ref && ref.parentNode) ref.parentNode.insertBefore(ui, ref);
    else box.appendChild(ui);
    box.classList.add('shc-proxied');
    sync.box = box;
    syncers.push(sync);
    sync();
  }

  /* Easify redraws its own options when a condition changes, which takes the
     buttons with it, so this runs again whenever the slot changes. proxyOne
     skips anything already done, so it is cheap to call. */
  function proxyEasify() {
    var slot = root.querySelector('[data-shc-easify]');
    if (!slot) return;
    [].forEach.call(slot.querySelectorAll('.tpo_option-container'), proxyOne);
  }

  /* Easify hides an option that its conditional logic says does not apply, by
     putting tpo_hidden on it rather than removing it. A product where every
     option is hidden, a timber shelter with nothing to powder coat for
     instance, would otherwise show a "Colour and extras" heading with nothing
     under it. Show the block only when there is something in it. */
  function showExtras() {
    var slot = root.querySelector('[data-shc-easify]');
    var wrap = slot && slot.closest('[data-shc-easifywrap]');
    if (!wrap || !slot) return;
    var boxes = slot.querySelectorAll('.tpo_option-container');
    var any = false;
    [].forEach.call(boxes, function (c) {
      if (!c.classList.contains('tpo_hidden')) any = true;
    });
    wrap.hidden = !any;
  }

  function watchEasify() {
    var slot = root.querySelector('[data-shc-easify]');
    if (!slot || !window.MutationObserver) return;
    var t = null;
    /* Attributes as well as children: the app shows and hides its options by
       class, so a class change is the signal that the block may now be empty
       or may have something in it again. */
    new MutationObserver(function () {
      if (proxying) return;
      clearTimeout(t);
      t = setTimeout(function () { proxyEasify(); resyncProxies(); showExtras(); }, 120);
    }).observe(slot, {
      childList: true, subtree: true,
      attributes: true, attributeFilter: ['class']
    });
  }

  /* ---------------- rendering ---------------- */
  function optCard(pos, value, made, on, extra) {
    var cls = 'shc-opt' + (extra && extra.cls ? ' ' + extra.cls : '');
    var sw = swatch(value);
    var inner = (sw ? '<span class="shc-sw ' + esc(sw) + '"></span>' : '') +
      '<b>' + esc(label(value)) + '</b>' +
      (note(value) ? '<span>' + esc(note(value)) + '</span>' : '') +
      (extra && extra.up ? '<em>' + extra.up + '</em>' : '');
    return '<button type="button" class="' + cls + '" data-shc-pick="' + pos +
      '" data-v="' + esc(value) + '" aria-pressed="' + (on ? 'true' : 'false') + '"' +
      (made ? '' : ' data-made="0" title="Not made in this combination"') +
      '>' + inner + '</button>';
  }

  /* The price of this option set to that value, everything else as it is. */
  function priceOf(pos, val) {
    var probe = chosen.slice(); probe[pos] = val;
    var v = variantFor(probe) ||
            S.variants.filter(function (x) { return x.o[pos] === val; })[0];
    return v ? v.p : null;
  }

  /* On a yes and no the figure that matters is what saying yes costs, not
     what the whole shelter costs over the cheapest one in the range. So it is
     measured against the No, which keeps it right whatever else is chosen. */
  function deltaFrom(pos, noVal, val) {
    var a = priceOf(pos, noVal), b = priceOf(pos, val);
    return (a != null && b != null && b > a) ? '+' + money(b - a) : '';
  }

  /* Lee's rule applied to the Shopify options too, not just the Easify ones:
     an option offering "Not Required" and one other thing reads as a Yes and
     a No. Side Panels, Cycle Rack, Installation, Roll back strip and Optional
     End Panels are all built that way. Where there is more than one real
     choice, Yes opens them underneath.

     These are ordinary option cards underneath, so the existing click
     handling picks them up and nothing else had to change. */
  function ynStep(pos, noVal, real) {
    var on = chosen[pos];
    var isNo = on === noVal;
    var pick = isNo ? real[0] : on;
    var one = real.length === 1;
    var up = deltaFrom(pos, noVal, pick);

    var html = '<div class="shc-opts shc-opts--2">' +
      '<button type="button" class="shc-opt" data-shc-pick="' + pos + '" data-v="' + esc(noVal) +
        '" aria-pressed="' + (isNo ? 'true' : 'false') + '"><b>' + esc(COPY.ynNo || 'No') +
        '</b><span>' + esc(COPY.ynNoNote || 'Not required') + '</span></button>' +
      '<button type="button" class="shc-opt" data-shc-pick="' + pos + '" data-v="' + esc(pick) +
        '" aria-pressed="' + (isNo ? 'false' : 'true') + '"><b>' + esc(COPY.ynYes || 'Yes') + '</b>' +
        /* "Yes, Required" says nothing, so the note is dropped where the only
           real value is a bare Required or Yes. */
        (one && /^(required|yes)$/i.test(plain(real[0])) ? ''
          : '<span>' + esc(one ? plain(label(real[0]))
              : fill(COPY.ynChoose || '{n} to choose from', { n: real.length })) + '</span>') +
        (up ? '<em>' + up + '</em>' : '') +
      '</button></div>';

    if (one) return html;

    var cols = real.length > 3 ? 'shc-opts--4' : real.length === 3 ? 'shc-opts--3' : 'shc-opts--2';
    return html + '<div class="shc-ynpick shc-opts ' + cols + '"' + (isNo ? ' hidden' : '') + '>' +
      real.map(function (v) {
        return optCard(pos, v, true, on === v, { up: deltaFrom(pos, noVal, v) });
      }).join('') + '</div>';
  }

  function buildSteps() {
    /* Options are sorted by role, so a Fixing step always lands in the same
       place whatever the product calls it, and anything unmapped falls to the
       end rather than vanishing. */
    var order = S.opts.map(function (o, i) { return { o: o, i: i }; })
      .sort(function (a, b) {
        return roleRank(a.o.role) - roleRank(b.o.role) || a.i - b.i;
      });

    var base = null;
    S.variants.forEach(function (v) { if (base == null || v.p < base) base = v.p; });

    var html = order.map(function (row, n) {
      var o = row.o, pos = row.i;
      var head = '<div class="shc-step"><span class="shc-lab"><span class="shc-no">' + (n + 1) +
        '</span>' + esc(COPY.roleLabel && COPY.roleLabel[o.role] ? COPY.roleLabel[o.role] : o.name) +
        '</span>';

      var noVal = null, real = [];
      o.values.forEach(function (v) {
        if (!noVal && NO_VALUE.test(plain(v))) { noVal = v; return; }
        real.push(v);
      });
      if (noVal && real.length) return head + ynStep(pos, noVal, real) + '</div>';

      var cards = o.values.map(function (val) {
        var probe = chosen.slice(); probe[pos] = val;
        var v = variantFor(probe);
        /* No exact match yet is not "unavailable": the click will move the
           other options to the nearest real combination. Only grey out a value
           that no variant has at all. */
        var anywhere = S.variants.some(function (x) { return x.o[pos] === val; });
        var up = '';
        if (v && base != null && v.p > base) up = '+' + money(v.p - base);
        return optCard(pos, val, anywhere, chosen[pos] === val,
          { up: up, cls: o.role === 'FINISH' ? 'shc-opt--sw' : '' });
      }).join('');

      var cols = o.values.length > 3 ? 'shc-opts--4'
               : o.values.length === 3 ? 'shc-opts--3' : 'shc-opts--2';
      return head + '<div class="shc-opts ' + cols + '">' + cards + '</div></div>';
    }).join('');

    /* The steps container only exists when the product has real variants: the
       section wraps it in {% if product.has_only_default_variant == false %}.
       On a single Default Title product querySelector returns null, and this
       assignment threw TypeError: Cannot set properties of null. The throw
       happened inside paint() BEFORE the price was written, so the price, the
       inc-VAT price, the configuration line, the stock line, the delivery
       window, the SKU, the sticky bar and the Easify extras all died with it.
       Eight live products were affected on 13 Oct 2026, three of them the
       whole trolley shelter range. The variant itself always resolved: seed()
       picks ["Default Title"] and variantFor matches it, so the correct price
       was in memory the whole time and simply never printed.

       Guarded here rather than by moving the div out of the Liquid condition:
       this asset is byte-identical across themes while the section is not, and
       the new branch can only be taken when the node is already absent, which
       today means the script has already died. */
    var stepsEl = root.querySelector('[data-shc-steps]');
    if (stepsEl) stepsEl.innerHTML = html;
  }

  function lineText(v) {
    var parts = [];
    S.opts.forEach(function (o, i) {
      if (String(o.name).toLowerCase() === 'title') return;
      var t = plain(label(chosen[i]));
      /* A "Not Required" answer is not part of the specification, and a bare
         "Required" means nothing on its own, so that one reads as the thing
         itself: "Installation" rather than "Required". */
      if (!t || NO_VALUE.test(t)) return;
      if (/^(required|yes)$/i.test(t)) {
        t = (COPY.roleLabel && COPY.roleLabel[o.role]) || o.name;
      }
      parts.push(t);
    });
    easifySummary().forEach(function (x) { parts.push(x); });
    return parts.length ? parts.join(' · ') : (S.title || '');
  }

  function paint() {
    var v = current();
    var addon = easifyAddonPence();
    var unit = (v ? v.p : 0) + addon;
    var qty = Math.max(1, parseInt((root.querySelector('[data-shc-qty]') || {}).value, 10) || 1);
    var line = lineText(v);

    /* The number written into [data-shc-price], in pence and before any
       formatting. Everything in the price column that states money is
       derived from this, so nothing can drift away from the headline. */
    var shownPence = unit * qty;

    buildSteps();

    root.querySelector('[data-shc-line]').textContent = line;
    root.querySelector('[data-shc-price]').textContent = v ? headline(shownPence) : '';
    root.querySelector('[data-shc-altprice]').textContent = v ? secondary(shownPence) : '';

    var st = root.querySelector('[data-shc-stock]');
    if (st) st.innerHTML = stockHtml(v) + delivHtml(v);

    /* ---- the offer pill ----

       One element, one string: "October Offer - Save £150.00 Was £3,663.01"
       with the old price struck through. Hidden whenever the variant has no
       compare at price. Do not add a second element beside it: that is what
       went wrong on the first attempt.

       THE WAS FIGURE IS DERIVED, NOT LOOKED UP.

       It used to be headline(v.cap * qty), the bare variant compare-at. That
       ignores the Easify extras, which the headline price does include, so
       adding a cigarette bin and a bench to the Marquis gave a headline of
       £3,417.02 against a Was of £3,052.51: an old price LOWER than the price
       being charged, which reads as a price rise.

       was = what is on screen + the saving. By construction the pill can
       never contradict the figure above it, whatever options are on and
       whatever the quantity is. The saving itself is unaffected by extras
       because they cancel out of (cap - p), so it stays the honest number.

       Both figures go through headline(), so both follow the VAT toggle
       together. The wording is escaped before the £ figures are concatenated
       in, so a setting cannot inject markup while the <s> still works. */
    var pill = root.querySelector('[data-shc-promo]');
    if (pill) {
      if (v && v.cap > v.p) {
        var savePence = (v.cap - v.p) * qty;
        pill.innerHTML =
          esc(COPY.promoLabel || '') + ' - ' +
          esc(fill(COPY.promoSaving || 'Save {amount}',
                   { amount: headline(savePence) })) + ' ' +
          esc(COPY.promoWas || 'Was') +
          ' <s>' + headline(shownPence + savePence) + '</s>';
        pill.hidden = false;
      } else {
        pill.innerHTML = '';
        pill.hidden = true;
      }
    }

    /* Breakdown. Only shown once something has been added on top, otherwise it
       is a one-line table saying the price twice. */
    var tb = root.querySelector('[data-shc-totals]');
    if (tb) {
      if (addon > 0 && v) {
        /* One line per extra, named, rather than a single "options added"
           figure that leaves the customer working out what they are paying
           for. Anything the app priced without spelling out falls into a
           remainder line, so the breakdown always adds up to the total. */
        var rows = '', named = 0;
        easifyLines().forEach(function (l) {
          if (!l.pence) return;
          named += l.pence;
          rows += '<div><span>' + esc(l.value) + '</span><b>' +
            headline(l.pence * qty) + '</b></div>';
        });
        if (addon - named > 0) {
          rows += '<div><span>' + esc(COPY.addonLabel || 'Options added') +
            '</span><b>' + headline((addon - named) * qty) + '</b></div>';
        }
        tb.hidden = false;
        tb.innerHTML =
          '<div><span>' + esc(COPY.baseLabel || 'Shelter, as specified') + '</span><b>' +
            headline(v.p * qty) + '</b></div>' + rows +
          '<div class="shc-tot"><span>' + esc(COPY.totalLabel || 'Total') +
            '</span><b>' + headline(shownPence) + '</b></div>';
      } else {
        tb.hidden = true;
        tb.innerHTML = '';
      }
    }

    var sku = root.querySelector('[data-shc-sku]');
    if (sku) { sku.textContent = (v && v.sku) ? v.sku : ''; sku.hidden = !(v && v.sku); }

    /* Never disabled for an unanswered option. Only a variant that does not
       exist at all turns the button off, and that cannot normally happen
       because choose() always lands on a real one. */
    root.querySelectorAll('[data-shc-add]').forEach(function (b) { b.disabled = !v; });
    var q = root.querySelector('[data-shc-quote]');
    if (q) q.disabled = !v;

    var sl = root.querySelector('[data-shc-sticky-line]');
    if (sl) sl.textContent = v ? line : '';
    var sp = root.querySelector('[data-shc-sprice]');
    if (sp) sp.innerHTML = v ? stickyPriceHtml(unit) : '';
    var sd = root.querySelector('[data-shc-sdate]');
    if (sd) { var w = window_(v); if (w) sd.textContent = fmt(w.early); }

    syncVariantInput();
    updateSticky();
  }

  /* ---------------- sticky bar, the store's own ---------------- */
  function stickyPriceHtml(p) {
    var inc = Math.round(p * (1 + VAT_RATE));
    return '<div><span class="sticky-price--ex">' + money(p) +
      '</span><span class="sticky-price--ex-label">ex VAT</span></div>' +
      '<div><span class="sticky-price--inc">' + money(inc) +
      '</span><span class="sticky-price--inc-label">inc VAT</span></div>';
  }
  var past = false;
  function updateSticky() {
    var bar = root.querySelector('[data-shc-sticky]');
    if (!bar) return;
    bar.classList.toggle('is-visible', !!(past && current()));
    if (past) alignTrustMiddle();
  }
  function alignTrustMiddle() {
    var box = root.querySelector('.sticky-delivery-box');
    var strip = root.querySelector('.sticky-atc-bar__trust-strip');
    var mid = root.querySelector('[data-shc-trustmid]');
    if (!box || !strip || !mid) return;
    var b = box.getBoundingClientRect(), s = strip.getBoundingClientRect();
    if (!b.width) return;
    mid.style.left = ((b.left + b.width / 2) - s.left) + 'px';
  }
  function initSticky() {
    var btn = root.querySelector('[data-shc-main-add]');
    if (!btn) return;
    var ticking = false;
    function measure() {
      ticking = false;
      past = btn.getBoundingClientRect().bottom < 0;
      updateSticky();
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; requestAnimationFrame(measure); }
    }, { passive: true });
    window.addEventListener('resize', function () { measure(); alignTrustMiddle(); }, { passive: true });
    measure();
  }
/* Easify's required inputs live in form[id^="sfd-easify-form-"]. The add
   button sits outside that form and the click is delegated on root, so
   Easify's validator never sees it. Check the form's own elements here. */
function easifyMissing() {
  var missing = null;
  document.querySelectorAll('form[id^="sfd-easify-form-"]').forEach(function (f) {
    [].forEach.call(f.elements, function (el) {
      var isRequired = el.required || el.dataset.required === '1';
if (missing || el.disabled || !isRequired) return;
      var c = el.closest('.tpo_option-container');
      if (c && c.classList.contains('tpo_hidden')) return;
      if (el.type === 'radio') {
        var any = [].some.call(f.elements, function (x) { return x.name === el.name && x.checked; });
        if (!any) missing = c || el;
      } else if (el.type === 'checkbox') {
        if (!el.checked) missing = c || el;
      } else if (String(el.value == null ? '' : el.value).trim() === '') {
        missing = c || el;
      }
    });
  });
  return missing;
}

function blockForRequired(node) {
  var err = root.querySelector('[data-shc-err]');
  if (err) {
    err.textContent = COPY.requiredMsg || 'Please choose the required options before adding to basket.';
    err.hidden = false;
  }
  try { node.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) {}
}
  /* ---------------- basket and quote ---------------- */
  function openCartDrawer() {
    try {
      if (window.FoxTheme && window.FoxTheme.pubsub && window.FoxTheme.pubsub.publish) {
        window.FoxTheme.pubsub.publish('cart-update', { source: 'sfd-shc' });
      }
    } catch (e) { /* the drawer is cosmetic, never block the add on it */ }
    var cd = document.querySelector('cart-drawer');
    if (!cd) return false;
    try {
      if (typeof cd.onCartRefresh === 'function') cd.onCartRefresh();
      if (typeof cd.show === 'function') { cd.show(); return true; }
    } catch (e) { return false; }
    return false;
  }

  function addToCart(btn) {
    var v = current();
    if (!v) return;
    var bad = easifyMissing();
    if (bad) { blockForRequired(bad); return; }
    var qty = Math.max(1, parseInt(root.querySelector('[data-shc-qty]').value, 10) || 1);
    var err = root.querySelector('[data-shc-err]');
    var label0 = btn.textContent;
    var all = root.querySelectorAll('[data-shc-add]');
    all.forEach(function (b) { b.disabled = true; });
    btn.textContent = 'Adding…';
    if (err) err.hidden = true;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: v.id, quantity: qty, properties: easifyProps() }] })
    }).then(function (r) {
      return r.json().then(function (j) { return { ok: r.ok, body: j }; });
    }).then(function (res) {
      if (!res.ok) throw new Error((res.body && res.body.description) || 'Could not add that to the basket.');
      btn.textContent = label0;
      all.forEach(function (b) { b.disabled = false; });
      if (AFTER_ADD === 'cart') { window.location.href = '/cart'; return; }
      if (!openCartDrawer()) {
        btn.textContent = 'Added';
        setTimeout(function () { btn.textContent = label0; }, 1600);
      }
    }).catch(function (e) {
      if (err) { err.textContent = e.message; err.hidden = false; }
      btn.textContent = label0;
      all.forEach(function (b) { b.disabled = false; });
    });
  }

  function quote() {
    var v = current();
    if (!v) return;
    var bad = easifyMissing();
    if (bad) { blockForRequired(bad); return; }
    if (typeof window.addToQuote !== 'function') {
      if (QUOTE_URL) window.location.href = QUOTE_URL;
      return;
    }
    var qty = Math.max(1, parseInt(root.querySelector('[data-shc-qty]').value, 10) || 1);
    window.addToQuote(v.id, qty, S.title, lineText(v),
      v.p + easifyAddonPence(), S.img, S.url, easifyProps());
  }

  /* ---------------- events ---------------- */
  root.addEventListener('click', function (e) {
    var pick = e.target.closest('[data-shc-pick]');
    if (pick) {
      choose(parseInt(pick.getAttribute('data-shc-pick'), 10), pick.getAttribute('data-v'));
      paint();
      return;
    }
    var step = e.target.closest('[data-shc-step]');
    if (step) {
      var input = step.parentElement.querySelector('input');
      input.value = Math.max(1, (parseInt(input.value, 10) || 1) +
        parseInt(step.getAttribute('data-shc-step'), 10));
      syncQty(input);
      return;
    }
    if (e.target.closest('[data-shc-quote]')) { quote(); return; }
    var add = e.target.closest('[data-shc-add]');
    if (add) { addToCart(add); return; }
    var rev = e.target.closest('[data-shc-reviews]');
    if (rev) {
      e.preventDefault();
      var t = document.querySelector('.jdgm-cards-carousel, .jdgm-widget');
      if (t) t.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });

  function syncQty(from) {
    var n = Math.max(1, parseInt(from.value, 10) || 1);
    root.querySelectorAll('[data-shc-qty],[data-shc-sqty]').forEach(function (i) {
      if (i !== from) i.value = n;
    });
    paint();
  }

  root.addEventListener('input', function (e) {
    if (e.target.matches('[data-shc-qty],[data-shc-sqty]')) syncQty(e.target);
  });

  document.addEventListener('sfd:vat', paint);

  fillMetaTitles();
  seed();
  paint();
  initSticky();
  mountEasify();
})();
